import styled from "styled-components";

export const LandingPageStyle = styled.main`
  width: 100%;

  /* Header Styles */
  header {
    width: 100%;
    border-bottom: 1px solid var(--grey-200);
    background: var(--white);

    .header-container {
      max-width: 80rem;
      margin: 0 auto;
      padding: 1rem;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .logo {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--primary-600);
        text-decoration: none;
        cursor: pointer;

        &:hover {
          color: var(--primary-700);
          transition: color 0.3s ease;
        }
      }

      nav {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.75rem;

        .nav-link {
          min-height: 2.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
          color: var(--grey-700);
          font-weight: 700;
          letter-spacing: 0;
          text-decoration: none;
          cursor: pointer;
          padding: 0 0.85rem;

          &:hover {
            color: var(--primary-600);
            background: var(--primary-50);
            transition: color 0.3s ease, background 0.3s ease;
          }
        }

        .nav-link-primary {
          background: var(--primary-600);
          color: var(--white);
          box-shadow: var(--shadow-2);

          &:hover {
            background: var(--primary-700);
            color: var(--white);
          }
        }
      }
    }
  }

  @media (max-width: 34rem) {
    header {
      .header-container {
        align-items: flex-start;
        gap: 0.8rem;
        flex-direction: column;

        nav {
          width: 100%;
          justify-content: stretch;

          .nav-link {
            flex: 1;
            padding: 0 0.6rem;
            text-align: center;
          }
        }
      }
    }
  }

  /* Hero Section */
  .hero {
    width: 100%;
    background: linear-gradient(to bottom, #eff6ff, #ffffff);
    padding: 3rem 1rem;

    .hero-container {
      max-width: 80rem;
      margin: 0 auto;

      h1 {
        font-size: clamp(1.875rem, 5vw, 3.75rem);
        font-weight: 700;
        text-align: center;
        color: var(--grey-900);
        margin: 1rem 0;
        line-height: 1.2;
      }
    }
  }

  /* Content Section */
  .content-section {
    width: 100%;
    padding: 3rem 1rem;

    .content-container {
      max-width: 80rem;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 1fr;
      gap: 2rem;
      align-items: center;

      .video-shell {
        aspect-ratio: 1 / 1;
        position: relative;
        border-radius: 0.5rem;
        overflow: hidden;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 55%, #ffffff 100%);

        .landing-video {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
          background: var(--black);
        }

        .video-cover {
          position: absolute;
          inset: 0;
          border: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          background: linear-gradient(160deg, rgba(219, 234, 254, 0.96) 0%, rgba(239, 246, 255, 0.96) 65%, rgba(255, 255, 255, 0.96) 100%);
        }

        .cover-logo {
          width: min(7.25rem, 40%);
          max-width: 100%;
          filter: drop-shadow(0 12px 18px rgba(37, 99, 235, 0.16));
        }

        .cover-copy {
          max-width: 28rem;

          .cover-chip {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.3rem 0.7rem;
            margin-bottom: 0.7rem;
            border-radius: 999px;
            background: rgba(37, 99, 235, 0.12);
            color: var(--primary-700);
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }

          h3 {
            color: var(--grey-900);
            font-size: clamp(1.2rem, 2vw, 1.65rem);
            margin-bottom: 0;
          }
        }

        .play-badge {
          margin-top: 0.35rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 5.5rem;
          padding: 0.55rem 1rem;
          border-radius: 999px;
          background: var(--primary-600);
          color: var(--white);
          font-size: 0.9rem;
          font-weight: 600;
          box-shadow: 0 8px 16px rgba(37, 99, 235, 0.25);
        }
      }

      .features {
        h2 {
          font-size: clamp(1.875rem, 4vw, 2.25rem);
          font-weight: 700;
          color: var(--grey-900);
          margin-bottom: 1.5rem;
        }

        ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;

          li {
            display: flex;
            gap: 1rem;

            svg {
              width: 1.5rem;
              height: 1.5rem;
              color: var(--primary-600);
              flex-shrink: 0;
              margin-top: 0.25rem;
            }

            div {
              h3 {
                font-size: 1.25rem;
                font-weight: 600;
                color: var(--grey-900);
                margin-bottom: 0.25rem;
              }

              p {
                color: var(--grey-600);
                font-size: 0.95rem;
              }
            }
          }
        }
      }

      @media (min-width: 64rem) {
        grid-template-columns: 1fr 1fr;

        .video-shell {
          order: 1;
        }

        .features {
          order: 2;
        }
      }
    }
  }

  /* App Badges Section */
  .app-badges {
    width: 100%;
    background-color: var(--grey-50);
    padding: 3rem 1rem;

    .badges-container {
      max-width: 80rem;
      margin: 0 auto;

      .badges-header {
        text-align: center;

        h2 {
          font-size: clamp(1.5rem, 4vw, 1.875rem);
          font-weight: 700;
          color: var(--grey-900);
          margin-bottom: 2rem;
        }
      }

      .badges-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
        justify-content: center;

        @media (min-width: 40rem) {
          flex-direction: row;
        }

        a {
          text-decoration: none;
          cursor: pointer;
          transition: transform 0.2s ease;

          &:hover {
            transform: scale(1.05);
          }

          .badge-button {
            background-color: var(--black);
            color: var(--white);
            padding: 0.75rem 1.5rem;
            border-radius: 0.5rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);

            svg {
              width: 2rem;
              height: 2rem;
            }

            .badge-text {
              text-align: left;

              .badge-small {
                font-size: 0.75rem;
              }

              .badge-large {
                font-size: 1.25rem;
                font-weight: 600;
                margin-top: -0.25rem;
              }
            }
          }
        }
      }
    }
  }

  .site-footer {
    width: 100%;
    border-top: 1px solid var(--grey-200);
    background: var(--white);

    .footer-container {
      max-width: 80rem;
      margin: 0 auto;
      padding: 1.25rem 1rem 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.65rem;
      align-items: center;
      text-align: center;

      p {
        margin: 0;
        color: var(--grey-600);
        font-size: 0.9rem;
      }

      .footer-links {
        display: flex;
        flex-wrap: wrap;
        gap: 0.85rem;
        justify-content: center;

        a {
          color: var(--primary-700);
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;
